<template>
  <div class="multiselect-main" :class="{ 'is-disabled': isDisabled }">
    <div class="multiselect-field" :class="{ 'is-disabled': isDisabled, 'is-compact': isCompact, 'has-selections': selectedObject.length > 0, 'is-placeholder-floated': selectedObject.length > 0 || inputFocused }">
      <Dropdown
        ref="dropdown"
        class="multiselect-dropdown"
        :items="checklist ? items : availableItems"
        :itemKey="itemKey"
        :selectedObject.sync="dropdownSelectedObject"
        :search.sync="searchText"
        :allowFreeText="allowFreeText"
        :validator="validator"
        :placeholder="placeholder"
        :isRequired="isRequired"
        :isDisabled="isDisabled"
        :errorMessage.sync="privateErrorMessage"
        :keepOpenOnSelect="checklist"
        :pinCheckedItems="checklist"
        :isItemChecked="checklist ? isOptionSelected : null"
        @selected="checklist ? toggleItem($event) : addItem($event)"
        @freeTextSelected="addFreeText"
        @focusChanged="inputFocusChanged"
        @keydown="dropdownKeyDown"
      >
        <template slot="prefix">
          <div v-show="selectedObject.length > 0" class="multiselect-chips" ref="chipList">
            <button
              v-for="(selectedValue, index) in visibleSelectedItems"
              :key="`chip-${index}`"
              type="button"
              class="multiselect-chip"
              :title="getSelectedOptionTitle(selectedValue)"
              :aria-label="'Remove ' + getSelectedOptionLabel(selectedValue)"
              :disabled="isDisabled"
              @mousedown.prevent
              @click="removeItem(selectedValue)"
            >
              <span class="multiselect-chip-label">{{ getSelectedOptionLabel(selectedValue) }}</span>
              <span v-if="!isDisabled" class="multiselect-chip-remove" aria-hidden="true">×</span>
            </button>
            <button
              v-if="hiddenSelectedItems.length > 0"
              type="button"
              class="multiselect-chip multiselect-chip-overflow"
              :title="getHiddenItemsTitle()"
              :disabled="isDisabled"
              @mousedown.prevent
              @click="openTable"
            >+{{ hiddenSelectedItems.length }}</button>
          </div>
        </template>
        <template slot="list-caption">
          <span v-if="checklist && !isDisabled" class="multiselect-caption-group">
            <span v-if="checklistSelectAll && checklistShownItems.length" class="multiselect-select-shown" @mousedown.prevent @click="toggleShownItems">
              <input type="checkbox" tabindex="-1" aria-hidden="true" :checked="allShownSelected" :indeterminate.prop="someShownSelected && !allShownSelected" />
              Select all shown ({{ checklistShownItems.length }})
            </span>
            <span v-if="selectedObject.length > 0" class="multiselect-selected-count">
              {{ selectedObject.length }} selected &middot;
              <a class="multiselect-list-clear" @mousedown.prevent @click="clearAll">Clear all</a>
            </span>
          </span>
          <span v-else-if="selectedObject.length > 1 && !isDisabled" class="multiselect-list-caption">
            {{ selectedObject.length }} selected &middot;
            <a class="multiselect-list-clear" @mousedown.prevent @click="clearAll">Clear all</a>
          </span>
        </template>
        <template slot="label-suffix">
          <span v-if="selectedObject.length > 0" class="multiselect-count">{{ selectedObject.length }}</span>
        </template>
      </Dropdown>
      <button
        v-if="selectedObject.length > 1 && !isDisabled"
        type="button"
        class="multiselect-clear-btn"
        :title="`Clear ${selectedObject.length} selections (Shift + Backspace)`"
        :aria-label="`Clear all ${selectedObject.length} ${placeholder} selections`"
        @mousedown.prevent
        @click="clearAll"
      >
        <svg viewBox="0 0 12 12" aria-hidden="true"><path d="M2.5 2.5l7 7M9.5 2.5l-7 7" /></svg>
      </button>
      <button
        type="button"
        class="multiselect-query-btn"
        :disabled="isDisabled"
        tabindex="-1"
        title="Inspect options (Ctrl + Q)"
        @mousedown.prevent
        @click="openTable"
      >
        <img src="https://dsmlib.logistics.systems/Icons/MagnifyingGlass.png" alt="" />
      </button>
    </div>

    <div class="multiselect-chip-measurements" aria-hidden="true">
      <span v-for="(selectedValue, index) in selectedObject" :key="`measure-${index}`" ref="chipMeasurements" class="multiselect-chip">
        <span class="multiselect-chip-label">{{ getSelectedOptionLabel(selectedValue) }}</span>
        <button v-if="!isDisabled" type="button" class="multiselect-chip-remove">×</button>
      </span>
      <span ref="overflowChipMeasurement" class="multiselect-chip multiselect-chip-overflow">+{{ selectedObject.length }}</span>
    </div>

    <div v-if="showTable" ref="tablePopup" class="table-popup">
      <div class="content">
        <TableBox
          :tableData="tableItems"
          :title="`Inspect ${placeholder} options`"
          :selectedRow.sync="tableSelectedRows"
          :maxSelection="-1"
          :showSelectAll="true"
          :fitContentWidth="true"
          :hideColumns="tableBoxHideColumns"
        />
        <div class="button-container">
          <button type="button" @click="confirmTable">Confirm {{ tableSelectedRows.length }} items</button>
          <button type="button" class="cancel-button" :disabled="isDisabled || !tableSelectedRows.length" @click="tableSelectedRows = []">Clear All</button>
          <button type="button" class="cancel-button" @click="closeTable">Cancel</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data: function () {
    return {
      dropdownSelectedObject: null,
      searchText: '',
      inputFocused: false,
      showTable: false,
      tableSelectedRows: [],
      teleportedTable: null,
      visibleChipCount: 0,
      privateErrorMessage: this.errorMessage || '',
      resizeObserver: null,
    };
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    // Complete selected rows; use :selectedObject.sync.
    selectedObject: {
      type: Array,
      default: () => [],
    },
    itemKey: {
      type: String,
      default: 'label',
    },
    // Optional identity field; defaults to the display field (itemKey).
    valueKey: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: 'Add...',
    },
    tableBoxHideColumns: {
      type: Array,
      default: () => [],
    },
    isRequired: {
      type: Boolean,
      default: false,
    },
    allowFreeText: {
      type: Boolean,
      default: false,
    },
    validator: {
      type: String,
      default: '',
    },
    errorMessage: {
      type: String,
      default: '',
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    isCompact: {
      type: Boolean,
      default: false,
    },
    checklist: {
      type: Boolean,
      default: true,
    }, // Dropdown rows show checkboxes by default; set false to use the add-only list
    checklistSelectAll: {
      type: Boolean,
      default: false,
    }, // Opt-in (with checklist): caption checkbox that selects/deselects every row matching the current search
  },
  computed: {
    selectionKey() {
      return this.valueKey || this.itemKey;
    },
    tableItems() {
      const customItems = this.allowFreeText ? this.selectedObject
        .map((item) => this.normalizeSelectedItem(item))
        .filter((item) => !this.items.some((option) => this.optionValueMatches(option[this.selectionKey], item[this.selectionKey]))) : [];
      return [...this.items, ...customItems].map((item, index) => Object.assign({ '#': index + 1 }, item, { '#': index + 1 }));
    },
    availableItems() {
      return this.items.filter(
        (item) => !this.selectedObject.some((selectedItem) => this.optionValueMatches(this.getSelectedValue(selectedItem), item[this.selectionKey])),
      );
    },
    checklistShownItems() {
      // Mirrors DropdownV2 matching: rows whose itemKey contains the search text; all rows when the search is empty
      const search = String(this.searchText || '').trim().toLowerCase();
      return this.items.filter(
        (item) => !(item && (item['#'] === 0 || item['#'] === '0')) && (!search || String(item?.[this.itemKey] ?? '').toLowerCase().includes(search)),
      );
    },
    allShownSelected() {
      return this.checklistShownItems.length > 0 && this.checklistShownItems.every((item) => this.isOptionSelected(item));
    },
    someShownSelected() {
      return this.checklistShownItems.some((item) => this.isOptionSelected(item));
    },
    visibleSelectedItems() {
      return this.isCompact ? this.selectedObject.slice(0, this.visibleChipCount) : this.selectedObject;
    },
    hiddenSelectedItems() {
      return this.isCompact ? this.selectedObject.slice(this.visibleChipCount) : [];
    },
  },
  methods: {
    hasValue(value) {
      return value !== null && value !== undefined && value !== '';
    },
    optionValueMatches(optionValue, savedValue) {
      if (!this.hasValue(optionValue) || !this.hasValue(savedValue)) return optionValue === savedValue;
      return String(optionValue) === String(savedValue);
    },
    getSelectedValue(item) {
      return item && typeof item == 'object' ? item[this.selectionKey] : item;
    },
    normalizeSelectedItem(item) {
      if (item && typeof item == 'object') return item;
      return this.items.find((option) => this.optionValueMatches(option[this.selectionKey], item)) || {
        [this.selectionKey]: item,
        [this.itemKey]: item,
      };
    },
    getSelectedOption(item) {
      if (item && typeof item == 'object') return item;
      return this.items.find((option) => this.optionValueMatches(option[this.selectionKey], item));
    },
    getSelectedOptionLabel(item) {
      if (item && typeof item == 'object') return item[this.itemKey] ?? item[this.selectionKey] ?? '';
      const option = this.getSelectedOption(item);
      return option?.[this.itemKey] ?? item;
    },
    getSelectedOptionTitle(item) {
      const option = this.getSelectedOption(item);
      if (!option) return this.getSelectedOptionLabel(item);

      const details = [];
      Object.keys(option).forEach((key) => {
        if (['label', 'key', 'value'].includes(key) || !this.hasValue(option[key])) return;
        details.push(`${key}: ${option[key]}`);
      });
      return [this.getSelectedOptionLabel(option), ...details].join('\n');
    },
    addFreeText(value) {
      if (!this.allowFreeText || this.isDisabled || !value.trim()) return;
      const text = value.trim();
      const item = [...this.items, ...this.selectedObject.map((selected) => this.normalizeSelectedItem(selected))]
        .find((option) => String(option[this.itemKey]).trim().toLowerCase() === text.toLowerCase());
      this.addItem(item || { [this.itemKey]: text, [this.selectionKey]: text });
    },
    addItem(item) {
      if (!item || this.isDisabled) return;
      const value = item[this.selectionKey];
      if (this.selectedObject.some((selectedItem) => this.optionValueMatches(this.getSelectedValue(selectedItem), value))) return;
      this.$emit('update:selectedObject', [...this.selectedObject.map((selectedItem) => this.normalizeSelectedItem(selectedItem)), item]);
      this.clearError();
      this.$nextTick(() => {
        this.dropdownSelectedObject = null;
        this.searchText = '';
      });
    },
    isOptionSelected(item) {
      const value = item && item[this.selectionKey];
      return this.selectedObject.some((selectedItem) => this.optionValueMatches(this.getSelectedValue(selectedItem), value));
    },
    toggleItem(item) {
      if (!item || this.isDisabled) return;
      if (this.isOptionSelected(item)) {
        this.removeItem(item);
      } else {
        this.addItem(item);
      }
    },
    toggleShownItems() {
      if (this.isDisabled || !this.checklistSelectAll) return;
      const shown = this.checklistShownItems;
      const current = this.selectedObject.map((selectedItem) => this.normalizeSelectedItem(selectedItem));
      if (this.allShownSelected) {
        this.$emit(
          'update:selectedObject',
          current.filter((selectedItem) => !shown.some((item) => this.optionValueMatches(item[this.selectionKey], this.getSelectedValue(selectedItem)))),
        );
      } else {
        this.$emit('update:selectedObject', [...current, ...shown.filter((item) => !this.isOptionSelected(item))]);
      }
      this.clearError();
    },
    removeItem(item) {
      if (this.isDisabled) return;
      const value = this.getSelectedValue(item);
      this.$emit(
        'update:selectedObject',
        this.selectedObject
          .map((selectedItem) => this.normalizeSelectedItem(selectedItem))
          .filter((selectedItem) => !this.optionValueMatches(this.getSelectedValue(selectedItem), value)),
      );
      this.clearError();
    },
    clearAll() {
      if (this.isDisabled || !this.selectedObject.length) return;
      this.dropdownSelectedObject = null;
      this.searchText = '';
      this.tableSelectedRows = [];
      this.$emit('update:selectedObject', []);
      this.clearError();
    },
    clearError() {
      this.privateErrorMessage = '';
    },
    getHiddenItemsTitle() {
      return this.hiddenSelectedItems.map((item) => this.getSelectedOptionLabel(item)).join('\n');
    },
    inputFocusChanged(focused) {
      this.inputFocused = focused;
    },
    dropdownKeyDown(event) {
      if (this.allowFreeText && event.key === 'Enter' && this.searchText.trim() && !this.isDisabled) {
        event.preventDefault();
        this.$refs.dropdown.setBlur();
        return;
      }
      if (event.ctrlKey && event.key && event.key.toUpperCase() === 'Q') {
        event.preventDefault();
        this.openTable();
        return;
      }
      if (event.key === 'Backspace' && !this.searchText && this.selectedObject.length > 0 && !this.isDisabled) {
        event.preventDefault();
        if (event.shiftKey) {
          this.clearAll(); // Shift + Backspace clears everything
          return;
        }
        this.removeItem(this.selectedObject[this.selectedObject.length - 1]);
      }
    },
    openTable() {
      if (this.isDisabled) return;
      this.tableSelectedRows = this.selectedObject.map((selectedItem) => {
        const value = this.getSelectedValue(selectedItem);
        return this.tableItems.find((item) => this.optionValueMatches(item[this.selectionKey], value)) || this.normalizeSelectedItem(selectedItem);
      });
      this.showTable = true;
      this.$nextTick(() => {
        if (this.$refs.dropdown) this.$refs.dropdown.setBlur();
      });
    },
    confirmTable() {
      const selectedRows = this.tableSelectedRows.map((item) => {
        const value = this.getSelectedValue(item);
        const option = this.items.find((candidate) => this.optionValueMatches(candidate[this.selectionKey], value));
        return option || this.normalizeSelectedItem(item);
      });
      this.showTable = false;
      this.clearError();
      this.$nextTick(() => {
        this.$emit('update:selectedObject', selectedRows);
      });
    },
    closeTable() {
      this.showTable = false;
    },
    attachTableTeleport() {
      const table = this.$refs.tablePopup;
      if (!table || this.teleportedTable) return;
      table.style.position = 'fixed';
      table.style.zIndex = '99990';
      this.$getOverlayRoot().appendChild(table);
      this.teleportedTable = table;
    },
    detachTableTeleport() {
      if (this.teleportedTable && this.teleportedTable.parentNode) {
        this.teleportedTable.parentNode.removeChild(this.teleportedTable);
      }
      this.teleportedTable = null;
    },
    escapeCloseTable(event) {
      if (event.code === 'Escape') this.closeTable();
    },
    observeChipField() {
      const control = this.$refs.dropdown && this.$refs.dropdown.$el.querySelector('.input-control');
      if (!control || this.observedChipField === control) return;
      this.observedChipField = control;
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(this.remeasureChips);
        this.resizeObserver.observe(control);
      } else {
        window.addEventListener('resize', this.remeasureChips);
      }
      this.remeasureChips();
    },
    remeasureChips() {
      if (!this.isCompact) {
        this.visibleChipCount = this.selectedObject.length;
        return;
      }
      if (!this.selectedObject.length) {
        this.visibleChipCount = 0;
        return;
      }
      const control = this.$refs.dropdown && this.$refs.dropdown.$el.querySelector('.input-control');
      const chipNodes = this.$refs.chipMeasurements || [];
      const overflowChip = this.$refs.overflowChipMeasurement;
      if (!control || chipNodes.length !== this.selectedObject.length || !overflowChip) return;

      const availableWidth = Math.max(0, control.clientWidth - 43);
      const chipWidths = chipNodes.map((chip) => chip.getBoundingClientRect().width);
      const gap = 3;
      let visibleCount = this.selectedObject.length;
      const chipWidth = chipWidths.reduce((total, width) => total + width, 0) + Math.max(0, this.selectedObject.length - 1) * gap;
      if (chipWidth > availableWidth) {
        visibleCount = 0;
        for (let candidate = this.selectedObject.length - 1; candidate >= 0; candidate -= 1) {
          const hiddenCount = this.selectedObject.length - candidate;
          overflowChip.textContent = `+${hiddenCount}`;
          const overflowWidth = overflowChip.getBoundingClientRect().width;
          const candidateWidth = chipWidths.slice(0, candidate).reduce((total, width) => total + width, 0) + Math.max(0, candidate - 1) * gap;
          const requiredWidth = candidateWidth + overflowWidth + (candidate > 0 ? gap : 0);
          if (requiredWidth <= availableWidth || candidate === 0) {
            visibleCount = candidate;
            break;
          }
        }
      }
      overflowChip.textContent = `+${this.selectedObject.length}`;
      this.visibleChipCount = visibleCount;
    },
  },
  mounted() {
    this.$nextTick(this.observeChipField);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.escapeCloseTable);
    window.removeEventListener('resize', this.remeasureChips);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    this.detachTableTeleport();
  },
  watch: {
    selectedObject: {
      handler() {
        this.$nextTick(this.remeasureChips);
        if (this.checklist) {
          // checked rows sit above the splitter; re-split whenever the selection changes (ticks, chips, select all, clear)
          this.$nextTick(() => this.$refs.dropdown && this.$refs.dropdown.refreshItemGrouping());
        }
      },
      deep: true,
    },
    items() {
      this.$nextTick(this.remeasureChips);
    },
    isDisabled() {
      this.$nextTick(this.remeasureChips);
    },
    isCompact() {
      this.$nextTick(this.remeasureChips);
    },
    errorMessage(value) {
      this.privateErrorMessage = value;
    },
    privateErrorMessage(value) {
      this.$emit('update:errorMessage', value);
    },
    showTable(value) {
      if (value) {
        document.addEventListener('keydown', this.escapeCloseTable);
        this.$nextTick(() => this.attachTableTeleport());
      } else {
        document.removeEventListener('keydown', this.escapeCloseTable);
        this.detachTableTeleport();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.multiselect-main {
  width: 100%;
  position: relative;
}

.multiselect-field {
  display: flex;
  align-items: center;
  width: 100%;
  height: 25px;
  margin: 2px 0;

  > .multiselect-dropdown {
    flex: 1 1 auto;
    min-width: 0;
    width: auto;
    height: 25px;
    margin: 0;
  }

  &:not(.is-compact) {
    height: auto;
    min-height: 25px;
    align-items: flex-end;

    > .multiselect-dropdown {
      height: auto;
      min-height: 25px; // same resting height as a normal Dropdown
    }
  }

  .multiselect-chips {
    display: flex;
    flex: 0 1 auto;
    align-items: flex-end;
    height: 100%;
    gap: 3px;
    min-width: 0;
    max-width: calc(100% - 43px);
    overflow: hidden;
    white-space: nowrap;
  }

  &:not(.is-compact) .multiselect-chips {
    display: contents;
  }

  &.is-disabled .multiselect-chip {
    background-color: rgba(200, 200, 200, 0.35); // borderless chips need a fill to stay visible when disabled
    color: #444;
  }

  .multiselect-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 11px;
    height: 11px;
    margin-left: 4px;
    padding: 0 4px;
    border-radius: 6px;
    background-color: $btn-color;
    color: black;
    font-size: 8px;
    line-height: 11px;
  }

  .multiselect-clear-btn {
    // Small round × beside the magnifier, revealed on hover / focus so it takes no attention (or chip space) at rest
    display: flex;
    flex: 0 0 15px;
    align-items: center;
    justify-content: center;
    width: 15px;
    height: 15px;
    margin: 0 3px 0 0;
    padding: 0;
    position: relative;
    top: -3px;
    border: 0;
    border-radius: 50%;
    background-color: transparent;
    color: #777;
    cursor: pointer;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.12s, background-color 0.12s, color 0.12s;

    > svg {
      display: block;
      width: 9px;
      height: 9px;
      fill: none;
      stroke: currentColor;
      stroke-width: 1.8;
      stroke-linecap: round;
    }

    &:hover {
      background-color: #fdecec; // red tint signals a destructive action (beats the global button hover colour)
      color: #c62828;
    }

    &:focus {
      box-shadow: none;
    }

    &:focus-visible {
      opacity: 1;
      pointer-events: auto;
      box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.3);
    }
  }

  &:hover > .multiselect-clear-btn,
  &:focus-within > .multiselect-clear-btn {
    opacity: 1;
    pointer-events: auto;
  }

  .multiselect-query-btn {
    display: flex;
    flex: 0 0 15px;
    align-items: center;
    justify-content: center;
    width: 15px;
    height: 15px;
    margin: 0 2px 0 0;
    padding: 0;
    position: relative;
    top: -3px;
    border: 0;
    background: transparent;
    cursor: pointer;

    > img {
      display: block;
      width: 15px;
      height: 15px;
    }

    &:disabled {
      opacity: 0.35;
      cursor: default;
    }
  }
}

// Lives inside DropdownV2's list caption, which is teleported to the overlay root, so keep these top-level
.multiselect-list-caption {
  margin-right: auto;
  color: #555;
  white-space: nowrap;
}

// Checklist caption: one left-aligned group that wraps internally on narrow lists (count + clear icon stay right)
.multiselect-caption-group {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px 10px;
  margin-right: 8px;
  color: #555;
  min-width: 0;
}

.multiselect-select-shown,
.multiselect-selected-count {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

.multiselect-select-shown {
  cursor: pointer;

  > input {
    margin: 0;
    pointer-events: none;
  }
}

.multiselect-list-clear {
  color: #c62828;
  text-decoration: underline;
  cursor: pointer;
}

.multiselect-chip-measurements {
  position: absolute;
  top: 0;
  left: -10000px;
  display: flex;
  gap: 3px;
  width: max-content;
  visibility: hidden;
  pointer-events: none;

}

.multiselect-field:not(.is-compact) > .multiselect-dropdown ::v-deep .dropdown-input {
  height: auto;
  min-height: 25px; // keeps label + underline where Dropdown/Input put them; grows only when chips wrap
}

.multiselect-field > .multiselect-dropdown ::v-deep .placeholder-top,
.multiselect-field.is-placeholder-floated > .multiselect-dropdown ::v-deep .placeholder {
  top: -12px;
  height: 25px; // same box as InputV2's floated label, so it lands at the same height as Dropdown/Input labels
  align-items: center;
  font-size: 8px;
  opacity: 1;
}

.multiselect-field > .multiselect-dropdown ::v-deep .input-control {
  display: flex;
  align-items: flex-end;
  position: relative;
  top: -3px;
  width: 100%;
  height: 24px; // Match the rendered height of the standard underlined input.
  border-bottom: 1px solid rgba(128, 128, 128, 0.5);

  &.is-disabled {
    background-color: rgba(200, 200, 200, 0.2);
  }

  > input[type='text'],
  > input[type='password'],
  > input[type='number'] {
    flex: 1 1 40px;
    min-width: 40px;
    width: 0;
    height: 100%;
    position: static;
    border: 0;
    padding: 0 0 0 2px;
    background-color: transparent;
    transition: none;
  }

  > input:focus {
    outline: none;
  }

  &:focus-within {
    border-color: black;
  }

  > input:disabled {
    background-color: transparent;
  }
}

.multiselect-field:not(.is-compact) > .multiselect-dropdown ::v-deep .input-control {
  height: auto;
  min-height: 24px; // match InputV2's standard underlined input height (single row = exactly a DropdownV2)
  padding-top: 7px; // 7 + 16px chip row + 1px underline = 24px; also clears the floated label when rows wrap
  padding-left: 5px; // chip text (5px chip padding) starts at 10px, same as DropdownV2's text and label
  margin-bottom: 1px; // underline lands on the same row as Dropdown/Input underlines
  flex-wrap: wrap;
  gap: 3px;
}

.multiselect-field > .multiselect-dropdown ::v-deep .input-control > input {
  height: 16px !important; // same height as a chip row, so typing never makes a row taller
  padding-left: 5px !important; // typed text starts at 10px like DropdownV2 when there are no chips
  font-size: 12px; // same as DropdownV2's value text
}

.multiselect-field:not(.is-compact) > .multiselect-clear-btn,
.multiselect-field:not(.is-compact) > .multiselect-query-btn {
  top: -7px; // vertically centred with the dropdown arrow
}

.multiselect-field:not(.is-compact) > .multiselect-dropdown ::v-deep .arrow-container {
  top: auto;
  bottom: 5px; // same spot as Dropdown's arrow on a single row; follows the last row when chips wrap
}

.multiselect-field .multiselect-chip,
.multiselect-chip-measurements > .multiselect-chip {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 0;
  position: relative; // anchors the hover-only ×
  box-sizing: border-box;
  max-width: 100%;
  height: 16px; // fills the value area between the floated label and the underline
  padding: 0 5px;
  border: 0; // borderless solid fill reads cleaner at this size
  border-radius: 3px;
  background-color: #dbe2ff;
  appearance: none;
  -webkit-appearance: none;
  color: #111;
  cursor: pointer;
  font-size: 12px; // same as DropdownV2's value text
  font-family: inherit;
  font-weight: normal;
  line-height: 1;
  text-align: left;
  white-space: nowrap;

  &:disabled {
    opacity: 1;
    cursor: default;
  }
}

.multiselect-field .multiselect-chip-label,
.multiselect-chip-measurements .multiselect-chip-label {
  min-width: 0;
  max-width: none;
  overflow: hidden; // full text normally; ellipsis only if a chip is wider than the whole field
  text-overflow: ellipsis;
  white-space: nowrap;
}

.multiselect-field .multiselect-chip-remove,
.multiselect-chip-measurements .multiselect-chip-remove {
  // Slim × shown only on hover/keyboard focus. It sits on top of the chip's right end
  // (absolute, takes no width), so chips stay compact and never reflow when hovered.
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 50%;
  right: 2px;
  width: 12px;
  height: 12px;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 50%;
  transform: translateY(-50%);
  background-color: #dbe2ff; // same as the chip, so it cleanly covers the text end
  box-shadow: -3px 0 3px #dbe2ff; // soft fade over the last letter
  color: #555;
  cursor: pointer;
  font-size: 11px;
  line-height: 1;
  opacity: 0;
  pointer-events: none;

}

.multiselect-field .multiselect-chip:hover:enabled {
  background-color: #c9d3fb; // slightly darker on hover (overrides the global button hover colour)
}

.multiselect-field .multiselect-chip:hover > .multiselect-chip-remove,
.multiselect-field .multiselect-chip:focus-visible > .multiselect-chip-remove {
  opacity: 1;
  pointer-events: auto;
  background-color: #c9d3fb; // matches the hovered chip
  box-shadow: -3px 0 3px #c9d3fb;
}

.multiselect-field .multiselect-chip-overflow,
.multiselect-chip-measurements .multiselect-chip-overflow {
  height: 16px;
  border: 1px dashed rgba(120, 140, 220, 0.6);
  padding: 0 5px;
  border-style: dashed;
  background-color: #fff;
  color: #333;
  cursor: pointer;
}

.table-popup {
  position: fixed;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  font-size: 12px;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 99990;
  padding: 10px;
  padding-top: calc(#{$top-nav-height} + 10px);
  background-color: rgba(0, 0, 0, 0.3);

  > .content {
    position: relative;
    box-sizing: border-box;
    width: 90%;
    height: 70%;
    padding: 10px;
    border-radius: 5px;
    background-color: white;

    > .button-container {
      position: absolute;
      top: 100%;
      right: 10px;
      display: flex;
      width: fit-content;
      margin: 0 3px;

      > button {
        border-radius: 0;
        border-bottom-right-radius: 5px;
        border-bottom-left-radius: 5px;
        background-color: white;

        &:first-child {
          color: green;
        }

        &:last-child {
          margin-left: 20px;
          color: red;
        }
      }
    }
  }
}
</style>
